import { CdkTableModule } from '@angular/cdk/table';
import { isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    PLATFORM_ID,
    WritableSignal,
    afterNextRender,
    inject,
    signal,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective, provideRtIDBStorage } from '@rt-tools/core';
import { RtTableComponent } from '@rt-tools/ui-kit-v2';
import { PageHeaderComponent } from '@stair-craft/common/components';

const SECTION_IDS: readonly string[] = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8', 's9'];

/**
 * Window after a TOC click during which scroll-driven highlight updates are
 * suppressed — otherwise the smooth-scroll animation walks the highlight
 * through every section the camera passes.
 */
const TOC_CLICK_SUPPRESS_MS: number = 800;
const BEM_BLOCK: string = 'sc-privacy-page';

interface ICookieRow {
    name: string;
    purpose: string;
    provider: string;
    duration: string;
}

const COOKIE_COLUMNS: readonly string[] = ['name', 'purpose', 'provider', 'duration'];

const COOKIES: readonly ICookieRow[] = [
    {
        name: '_ym_uid',
        purpose: $localize`:@@privacyS4Cookie1Purpose:Идентификация визита`,
        provider: 'Яндекс.Метрика',
        duration: $localize`:@@privacyS4Cookie1Duration:1 год`,
    },
    {
        name: '_ym_d',
        purpose: $localize`:@@privacyS4Cookie2Purpose:Дата первого визита`,
        provider: 'Яндекс.Метрика',
        duration: $localize`:@@privacyS4Cookie2Duration:1 год`,
    },
    {
        name: '_ga',
        purpose: $localize`:@@privacyS4Cookie3Purpose:Уникальный ID`,
        provider: 'Google Analytics',
        duration: $localize`:@@privacyS4Cookie3Duration:2 года`,
    },
    {
        name: '_gid',
        purpose: $localize`:@@privacyS4Cookie4Purpose:Идентификация сеанса`,
        provider: 'Google Analytics',
        duration: $localize`:@@privacyS4Cookie4Duration:24 часа`,
    },
    {
        name: 'sc:last-stair-config',
        purpose: $localize`:@@privacyS4Cookie5Purpose:Последняя конфигурация лестницы между сессиями`,
        provider: 'GoldenCalc',
        duration: $localize`:@@privacyS4Cookie5Duration:бессрочно (до очистки)`,
    },
    {
        name: 'sc_consent_v1',
        purpose: $localize`:@@privacyS4Cookie6Purpose:Решение пользователя по cookie-баннеру`,
        provider: 'GoldenCalc',
        duration: $localize`:@@privacyS4Cookie6Duration:бессрочно (до очистки)`,
    },
    {
        name: 'cf_turnstile_*',
        purpose: $localize`:@@privacyS4Cookie7Purpose:Защита от ботов при отправке форм`,
        provider: 'Cloudflare',
        duration: $localize`:@@privacyS4Cookie7Duration:сеанс`,
    },
];

@Component({
    selector: 'sc-privacy-page',
    templateUrl: './privacy-page.component.html',
    styleUrl: './privacy-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        CdkTableModule,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtTableComponent,
        PageHeaderComponent,
    ],
    // The kit table asks for its column settings store even without a table id to keep them under;
    // the store opens nothing until asked, and only this page needs it.
    providers: [provideRtIDBStorage()],
    host: {
        class: BEM_BLOCK,
    },
})
export default class PrivacyPageComponent {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #hostEl: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

    #scrollHandler: (() => void) | null = null;
    #rafHandle: number | null = null;
    #suppressUntil: number = 0;

    protected readonly cookies: readonly ICookieRow[] = COOKIES;
    protected readonly cookieColumns: readonly string[] = COOKIE_COLUMNS;
    protected readonly activeSectionId: WritableSignal<string> = signal<string>('s1');

    constructor() {
        afterNextRender((): void => {
            if (!isPlatformBrowser(this.#platformId)) {
                return;
            }
            const sections: HTMLElement[] = SECTION_IDS.map((id: string): HTMLElement | null =>
                this.#hostEl.nativeElement.querySelector<HTMLElement>(`#${id}`)
            ).filter((el: HTMLElement | null): el is HTMLElement => el !== null);
            if (sections.length === 0) {
                return;
            }

            const updateActive: () => void = (): void => {
                this.#rafHandle = null;
                if (Date.now() < this.#suppressUntil) {
                    return;
                }
                this.activeSectionId.set(this.#computeActiveId(sections));
            };

            this.#scrollHandler = (): void => {
                if (this.#rafHandle !== null) {
                    return;
                }
                this.#rafHandle = globalThis.requestAnimationFrame(updateActive);
            };

            updateActive();
            globalThis.addEventListener('scroll', this.#scrollHandler, { passive: true });
            globalThis.addEventListener('resize', this.#scrollHandler, { passive: true });
        });

        this.#destroyRef.onDestroy((): void => {
            if (this.#scrollHandler !== null) {
                globalThis.removeEventListener('scroll', this.#scrollHandler);
                globalThis.removeEventListener('resize', this.#scrollHandler);
            }
            if (this.#rafHandle !== null) {
                globalThis.cancelAnimationFrame(this.#rafHandle);
            }
        });
    }

    protected onTocClick(event: MouseEvent, sectionId: string): void {
        event.preventDefault();
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const target: HTMLElement | null = this.#hostEl.nativeElement.querySelector<HTMLElement>(`#${sectionId}`);
        if (!target) {
            return;
        }
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.activeSectionId.set(sectionId);
        this.#suppressUntil = Date.now() + TOC_CLICK_SUPPRESS_MS;
        globalThis.history.replaceState(null, '', `#${sectionId}`);
    }

    /**
     * Active = the section whose heading (h2) is closest to but already at or
     * above the vertical center of the viewport. Same logic as terms page.
     */
    #computeActiveId(sections: readonly HTMLElement[]): string {
        const probeY: number = globalThis.innerHeight / 2;
        let bestId: string = sections[0].id;
        let bestTop: number = -Infinity;

        for (const section of sections) {
            const heading: HTMLElement | null = section.querySelector<HTMLElement>('h2');
            const top: number = (heading ?? section).getBoundingClientRect().top;
            if (top <= probeY && top > bestTop) {
                bestTop = top;
                bestId = section.id;
            }
        }
        return bestId;
    }
}
